import { DateInput } from "@msuite/ui/date-input";
import { Textarea } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { RadioSelect } from "@msuite/ui/radio-select";
import { moment } from "@msuite/utils/date";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import { calculateTourWaveCutoffTime, getTourWaveCutoffValidation } from "@msuite/utils/stock";
import { useFormContext, useWatch } from "react-hook-form";
import { ProjectSearch } from "@/components/projects";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useTourWaves } from "@/hooks/use-tour-waves";
import { useOrderFormContext } from "../context";

export function OrderFormDelivery() {
	/** Context */
	const { isProjectLocked, projectScope } = useOrderFormContext();
	const methods = useFormContext<OrderFormSchema>();
	const deliveryDate = useWatch({ control: methods.control, name: "deliveryDate" });
	const { tourWaves } = useTourWaves(deliveryDate);

	/** Hooks */
	const { t } = useTranslation();
	const mentionOptions = useMentionOptions();

	/** Render */
	return (
		<div className="grid grid-cols-[350px_1fr] gap-4">
			<ProjectSearch
				onChange={(projectId) => {
					if (projectId) methods.setValue("projectId", projectId);
				}}
				defaultProjectId={methods.getValues("projectId")}
				isDisabled={isProjectLocked}
				isNullable={false}
				scope={projectScope}
			/>
			<div className="col-start-1">
				<DateInput
					label={t("stock.delivery_date")}
					isRequired
					control={methods.control}
					name="deliveryDate"
				/>
			</div>
			{deliveryDate && (
				<div className="col-start-1">
					<RadioSelect
						label={t("stock.tour")}
						control={methods.control}
						name="tourWaveId"
					>
						<RadioSelect.Item value="default">{t("general.standard")}</RadioSelect.Item>
						{tourWaves.map((tourWave) => {
							const isDisabled = !getTourWaveCutoffValidation(deliveryDate, tourWave);
							const deadline = calculateTourWaveCutoffTime(
								moment(deliveryDate).setTime(tourWave.startTime),
								tourWave.cutoffOffset,
							);
							const deadlineString = `${deadline.format("dd, HH:mm")} ${t("dates.time_suffix")}`;
							return (
								<RadioSelect.Item
									value={tourWave.id}
									key={tourWave.id}
									isDisabled={isDisabled}
								>
									{tourWave.name} ({tourWave.startTime} - {tourWave.endTime}) ({t("stock.deadline")}
									: {deadlineString})
								</RadioSelect.Item>
							);
						})}
					</RadioSelect>
				</div>
			)}
			<div className="col-start-1 col-span-2">
				<Textarea
					isRequired={false}
					label={t("notes.note")}
					control={methods.control}
					name="note"
					placeholder={t("placeholders.add_note")}
					{...mentionOptions}
				/>
			</div>
		</div>
	);
}
